/**
 * Firestore access for `skidaCrmShareholders`, `skidaCrmIssuances` and
 * `skidaCrmTransfers`.
 *
 * Three tiny collections, loaded whole and kept live, like the partners and
 * tasks repositories beside it. All three are admin-only in `firestore.rules` —
 * a non-admin subscribing here gets a permission error rather than an empty
 * list, which is why the screen says who may see it rather than rendering
 * "nothing here".
 *
 * Holdings are never written. Every share figure in the app is derived from
 * these documents by `types/ownership.ts`; see the ledger note there.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  setDoc,
  writeBatch,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { toIsoOrNull } from '@/shared/util/dates'
import { strip } from '@/shared/util/strip'
import {
  MAIL_STATES,
  storedPersonnummer,
  OPTION_STATES,
  type Attachment,
  type Issuance,
  type OptionGrant,
  type OptionStatus,
  type SharePurchase,
  type Subscription,
  type HolderStatus,
  type ShareholderMail,
  type ShareholderSend,
  type ShareTransfer,
  type Shareholder,
} from '@/types/ownership'

const SHAREHOLDERS = 'skidaCrmShareholders'
const ISSUANCES = 'skidaCrmIssuances'
const TRANSFERS = 'skidaCrmTransfers'
const MAIL = 'skidaCrmShareholderMail'
const SENDS = 'skidaCrmEmailSends'

/**
 * The sends this screen is about.
 *
 * `skidaCrmEmailSends` holds the newsletter's campaigns too, and a mail log on
 * the share register that listed those would be showing 44 000-person sends on
 * a screen about a hundred and forty investors. Filtered on read rather than in
 * the query: four values, a collection of a few hundred, and a composite index
 * to keep in step for no gain.
 */
const REGISTER_AUDIENCES = new Set(['shareholders', 'issuance-subscribers'])

const str = (v: unknown) => (typeof v === 'string' ? v : '')
const isoDate = (v: unknown) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '')
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
/** A count is a whole number of things. A half share is a typo, not a holding. */
const count = (v: unknown) => Math.max(0, Math.round(num(v) ?? 0))

function mapPurchases(v: unknown): SharePurchase[] {
  if (!Array.isArray(v)) return []
  return v
    .map((p) => {
      const row = (p ?? {}) as Record<string, unknown>
      const note = str(row.note)
      return {
        issuanceId: str(row.issuanceId),
        shares: count(row.shares),
        // Null, not 0, when unset: 0 is a price somebody agreed and would make
        // the round look like it raised nothing from this holder.
        pricePerShare: num(row.pricePerShare),
        date: isoDate(row.date),
        /**
         * Spread in, never set to `undefined`.
         *
         * `note: undefined` is a *present key with no value*, which Firestore
         * refuses — and this one sits inside an array, where the top-level
         * strip on the way out cannot see it. Every shareholder holding a
         * purchase without a note became unsaveable the day the field was
         * added: "Unsupported field value: undefined".
         */
        ...(note ? { note } : {}),
        // Same rule as the note above it: spread in, never `undefined`.
        ...(isoDate(row.paidAt) ? { paidAt: isoDate(row.paidAt) } : {}),
      }
    })
    .filter((p) => p.shares > 0)
}

function mapOptions(v: unknown): OptionGrant[] {
  if (!Array.isArray(v)) return []
  return v
    .map((o) => {
      const row = (o ?? {}) as Record<string, unknown>
      const status = str(row.status)
      return {
        count: count(row.count),
        grantDate: isoDate(row.grantDate),
        strikePrice: num(row.strikePrice),
        expiryDate: isoDate(row.expiryDate),
        // Grants written before the 14 Aug round carry no status, and they are
        // all outstanding — a grant that had been exercised or had lapsed could
        // not have been recorded as such. So the absent value is `active`, not
        // an unknown to be shown as a blank pill.
        status: OPTION_STATES.some((s) => s.key === status)
          ? (status as OptionStatus)
          : 'active',
        exerciseDate: isoDate(row.exerciseDate),
        expiredDate: isoDate(row.expiredDate),
        exerciseIssuanceId: str(row.exerciseIssuanceId),
        exerciseResolution: str(row.exerciseResolution),
        // No paid date means the strike price has not landed, so the shares do
        // not exist yet. The absent value is the safe one in both directions:
        // an unpaid exercise counts nowhere, and nothing is silently promoted.
        paidDate: isoDate(row.paidDate),
      }
    })
    .filter((o) => o.count > 0)
}

/**
 * Files filed against a record — decision 54.
 *
 * A row with no `path` is dropped: the path is the only route back to the
 * bytes, so an entry without one is a name for a file nobody can open, and
 * showing it would claim the register holds a document it cannot produce.
 */
function mapAttachments(v: unknown): Attachment[] {
  if (!Array.isArray(v)) return []
  return v
    .map((a) => {
      const row = (a ?? {}) as Record<string, unknown>
      return {
        id: str(row.id),
        name: str(row.name),
        size: Math.max(0, num(row.size) ?? 0),
        uploadedAt: isoDate(row.uploadedAt),
        path: str(row.path),
      }
    })
    .filter((a) => a.id && a.path)
}

function mapShareholder(d: QueryDocumentSnapshot<DocumentData>): Shareholder {
  const v = d.data()
  return {
    // Written by scripts/geocodeAll.ts and by nothing else, so it is passed
    // through as stored — precision included, which is the half that matters.
    geo: (v.geo ?? undefined) as Shareholder['geo'],
    id: d.id,
    // A record written before the register held companies is a person, which is
    // what every one of them was.
    kind: str(v.kind) === 'company' ? 'company' : 'person',
    name: str(v.name),
    address: str(v.address),
    phone: str(v.phone),
    email: str(v.email).toLowerCase(),
    // Digits only *when the result is a Norwegian personnummer*. The register
    // holds fourteen foreign holders whose identifier is not eleven digits —
    // `FDRGST68M22H501Z`, `759-229-578 (Canada)`, `030476-0734` — and stripping
    // the non-digits from those destroys them silently, which is the worst way
    // to lose an identity document. Anything that does not normalise to eleven
    // digits is kept exactly as the source wrote it.
    // One rule, shared with the form — see `storedPersonnummer`.
    personnummer: storedPersonnummer(str(v.personnummer)),
    orgNumber: str(v.orgNumber).replace(/\D/g, ''),
    purchases: mapPurchases(v.purchases),
    options: mapOptions(v.options),
    linkedLeadId: str(v.linkedLeadId),
    linkedLeadContactId: str(v.linkedLeadContactId),
    linkedLeadContact: str(v.linkedLeadContact),
    linkedAmbassadorId: str(v.linkedAmbassadorId),
    linkedTeamEmail: str(v.linkedTeamEmail).toLowerCase(),
    linkedLabel: str(v.linkedLabel),
    attachments: mapAttachments(v.attachments),
    subscriptions: mapSubscriptions(v.subscriptions),
    // Written by the matcher and by nothing else; passed through as it stands.
    matches: (v.matches ?? undefined) as Shareholder['matches'],
    legalName: str(v.legalName) || undefined,
    altEmail: str(v.altEmail).toLowerCase() || undefined,
    contactPerson: str(v.contactPerson) || undefined,
    postalCode: str(v.postalCode) || undefined,
    city: str(v.city) || undefined,
    country: str(v.country) || undefined,
    birthNote: str(v.birthNote) || undefined,
    shareSeries: str(v.shareSeries) || undefined,
    entryDate: isoDate(v.entryDate) || undefined,
    vpsAccount: str(v.vpsAccount) || undefined,
    folkeinvestRef: str(v.folkeinvestRef) || undefined,
    nameVariants: str(v.nameVariants) || undefined,
    sources: str(v.sources) || undefined,
    createdAt: toIsoOrNull(v.createdAt) ?? undefined,
    updatedAt: toIsoOrNull(v.updatedAt) ?? undefined,
  }
}

/** Interest in an open round — see the note on `Subscription`. */
function mapSubscriptions(v: unknown): Subscription[] {
  if (!Array.isArray(v)) return []
  return v
    .map((x) => {
      const row = (x ?? {}) as Record<string, unknown>
      return {
        issuanceId: str(row.issuanceId),
        shares: count(row.shares),
        pricePerShare: num(row.pricePerShare),
        note: str(row.note),
      }
    })
    .filter((x) => x.issuanceId && (x.shares > 0 || x.note))
}

function mapIssuance(d: QueryDocumentSnapshot<DocumentData>): Issuance {
  const v = d.data()
  return {
    id: d.id,
    name: str(v.name),
    date: isoDate(v.date),
    pricePerShare: num(v.pricePerShare),
    // Absent means completed: every round predating Emisjon 4 was closed.
    status: str(v.status) === 'ongoing' ? 'ongoing' : 'completed',
    // Absent means not reported to Foretaksregisteret, which is what every
    // round is until somebody records the day it was.
    registeredAt: isoDate(v.registeredAt) || undefined,
    paymentRequestSentAt: toIsoOrNull(v.paymentRequestSentAt) ?? undefined,
    paymentRequestCount: num(v.paymentRequestCount) ?? undefined,
    attachments: mapAttachments(v.attachments),
    createdAt: toIsoOrNull(v.createdAt) ?? undefined,
    updatedAt: toIsoOrNull(v.updatedAt) ?? undefined,
  }
}

function mapTransfer(d: QueryDocumentSnapshot<DocumentData>): ShareTransfer {
  const v = d.data()
  return {
    id: d.id,
    date: isoDate(v.date),
    fromId: str(v.fromId),
    toId: str(v.toId),
    buyback: v.buyback === true,
    shares: count(v.shares),
    pricePerShare: num(v.pricePerShare),
    // Anything other than an explicit `true` is unsettled. A transfer whose flag
    // was lost would otherwise move shares nobody agreed to move.
    settled: v.settled === true,
    note: str(v.note),
    createdAt: toIsoOrNull(v.createdAt) ?? undefined,
    updatedAt: toIsoOrNull(v.updatedAt) ?? undefined,
  }
}

function mapMail(d: QueryDocumentSnapshot<DocumentData>): ShareholderMail {
  const v = d.data()
  const status = str(v.status)
  return {
    id: d.id,
    shareholderId: str(v.shareholderId),
    sendId: str(v.sendId),
    name: str(v.name),
    // Absent on the first payment requests, which predate the field. They were
    // all payment requests, so that is what absent means.
    kind: str(v.kind) || 'payment-request',
    group: str(v.group),
    subject: str(v.subject),
    issuanceId: str(v.issuanceId),
    issuanceName: str(v.issuanceName),
    email: str(v.email),
    shares: count(v.shares),
    amount: count(v.amount),
    sentAt: toIsoOrNull(v.sentAt) ?? '',
    sentBy: str(v.sentBy),
    // An unknown state means it went out and nothing has come back about it,
    // which is exactly what `sent` says.
    status: status in MAIL_STATES ? (status as ShareholderMail['status']) : 'sent',
    failure: str(v.failure) || undefined,
    openedAt: toIsoOrNull(v.openedAt) ?? undefined,
    opens: num(v.opens) ?? undefined,
    clicked: v.clicked === true,
  }
}

/**
 * One batch that went out to the register.
 *
 * `groups` is read defensively: it is written by the send function as an array
 * of holder statuses, and a payment request — which predates recipient groups
 * entirely — has none. An empty list means "whoever subscribed to the round",
 * which the issuance name beside it says better than a pill could.
 */
function mapSend(d: QueryDocumentSnapshot<DocumentData>): ShareholderSend {
  const v = d.data()
  const groups = Array.isArray(v.groups) ? v.groups.filter((g) => typeof g === 'string') : []
  return {
    id: d.id,
    subject: str(v.subject),
    html: str(v.html),
    groups: groups as HolderStatus[],
    issuanceId: str(v.issuanceId),
    issuanceName: str(v.issuanceName),
    audience: str(v.audience),
    total: count(v.total),
    sent: count(v.sent),
    failed: count(v.failed),
    attachmentCount: count(v.attachmentCount),
    status: str(v.status) || 'finished',
    startedAt: toIsoOrNull(v.startedAt) ?? '',
    // A draft has no `startedBy`; it has whoever last saved it.
    startedBy: str(v.startedBy) || str(v.savedBy),
    updatedAt: toIsoOrNull(v.updatedAt) ?? '',
    finishedAt: toIsoOrNull(v.finishedAt) ?? '',
    resendOf: str(v.resendOf),
    resentAs: str(v.resentAs),
    resentAt: toIsoOrNull(v.resentAt) ?? '',
  }
}

/**
 * Every send to the register, newest first.
 *
 * Its own subscription rather than a fifth arm of `subscribeToOwnership`: the
 * cap table is read on every visit to Ownership and this is read on one tab of
 * it, and holding the register hostage to a collection it does not need is how
 * a screen gets slower for a feature most visits never open.
 */
export function subscribeToShareholderSends(
  onData: (sends: ShareholderSend[]) => void,
  onError: (e: Error) => void,
): () => void {
  return onSnapshot(
    query(collection(db, SENDS)),
    (snap) => {
      onData(
        snap.docs
          .map(mapSend)
          .filter((s) => REGISTER_AUDIENCES.has(s.audience))
          // A draft sorts by when it was last touched, a send by when it went
          // out — both are "when did this last matter", which is the order a
          // log is read in.
          .sort((a, b) =>
            (b.startedAt || b.updatedAt).localeCompare(a.startedAt || a.updatedAt),
          ),
      )
    },
    (e) => onError(e as Error),
  )
}

/**
 * A letter to the register, saved before it is sent.
 *
 * Written to `skidaCrmEmailSends` with `status: 'draft'`, which is the one
 * shape of that document a browser may write — the rules whitelist the content
 * fields and refuse anything carrying figures, so a draft can never pass itself
 * off as a send that happened.
 *
 * **Attachments are not saved.** They are base64 and a Firestore document stops
 * at a megabyte, so a 900 KB file would not fit and a 400 KB one would fit
 * until the day it did not. The compose screen says so where the files are
 * picked rather than letting somebody discover it on reopening a draft.
 */
export interface ShareholderDraft {
  subject: string
  html: string
  groups: HolderStatus[]
  issuanceId: string
  savedBy: string
}

const draftDoc = (d: ShareholderDraft) => ({
  subject: d.subject,
  html: d.html,
  groups: d.groups,
  issuanceId: d.issuanceId,
  audience: 'shareholders',
  kind: 'service' as const,
  sender: 'consumer' as const,
  status: 'draft' as const,
  savedBy: d.savedBy,
})

export async function createShareholderDraft(d: ShareholderDraft): Promise<string> {
  const ref = await addDoc(collection(db, SENDS), {
    ...draftDoc(d),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateShareholderDraft(id: string, d: ShareholderDraft): Promise<void> {
  await updateDoc(doc(db, SENDS, id), { ...draftDoc(d), updatedAt: serverTimestamp() })
}

export async function deleteShareholderDraft(id: string): Promise<void> {
  await deleteDoc(doc(db, SENDS, id))
}

/**
 * All four collections in one subscription, because no screen wants one alone.
 *
 * The holdings on the Shareholders tab are already a function of the transfers,
 * so a screen holding the register without them would show pre-transfer numbers
 * until somebody clicked the third tab.
 */
export function subscribeToOwnership(
  onData: (d: {
    holders: Shareholder[]
    issuances: Issuance[]
    transfers: ShareTransfer[]
    mail: ShareholderMail[]
  }) => void,
  onError: (e: Error) => void,
): () => void {
  let holders: Shareholder[] = []
  let issuances: Issuance[] = []
  let transfers: ShareTransfer[] = []
  let mail: ShareholderMail[] = []
  let seenHolders = false
  let seenIssuances = false
  let seenTransfers = false
  let seenMail = false
  const emit = () => {
    if (seenHolders && seenIssuances && seenTransfers && seenMail) {
      onData({ holders, issuances, transfers, mail })
    }
  }

  // No `orderBy` on either: a Firestore sort drops documents missing the field,
  // and an issuance saved without a date would vanish rather than sort last.
  const unHolders = onSnapshot(
    query(collection(db, SHAREHOLDERS)),
    (snap) => {
      holders = snap.docs.map(mapShareholder).sort((a, b) => a.name.localeCompare(b.name))
      seenHolders = true
      emit()
    },
    (e) => onError(e as Error),
  )
  const unIssuances = onSnapshot(
    query(collection(db, ISSUANCES)),
    (snap) => {
      // Oldest first, sorted on read — decision 18. A share register is read as
      // a history: the seed round, then what followed. Insertion order would be
      // neither, since a round entered late sorts by when somebody typed it.
      issuances = snap.docs.map(mapIssuance).sort((a, b) => a.date.localeCompare(b.date))
      seenIssuances = true
      emit()
    },
    (e) => onError(e as Error),
  )

  const unTransfers = onSnapshot(
    query(collection(db, TRANSFERS)),
    (snap) => {
      // Newest first: the register is read from the most recent movement back.
      transfers = snap.docs.map(mapTransfer).sort((a, b) => b.date.localeCompare(a.date))
      seenTransfers = true
      emit()
    },
    (e) => onError(e as Error),
  )

  const unMail = onSnapshot(
    query(collection(db, MAIL)),
    (snap) => {
      // Newest first, as a record's history reads.
      mail = snap.docs.map(mapMail).sort((a, b) => b.sentAt.localeCompare(a.sentAt))
      seenMail = true
      emit()
    },
    (e) => onError(e as Error),
  )

  return () => {
    unHolders()
    unIssuances()
    unTransfers()
    unMail()
  }
}

type ShareholderInput = Omit<Shareholder, 'id' | 'createdAt' | 'updatedAt'>
type IssuanceInput = Omit<Issuance, 'id' | 'createdAt' | 'updatedAt'>
type TransferInput = Omit<ShareTransfer, 'id' | 'createdAt' | 'updatedAt'>

/**
 * Drop keys whose value is `undefined` before handing the object to Firestore.
 *
 * Firestore rejects `undefined` outright — the whole write fails with
 * "Unsupported field value" and names one field, even though the object is
 * otherwise fine. The optional fields on these records (`paymentRequestSentAt`,
 * `paymentRequestCount`, and the `linked*` ids) read back as `undefined`
 * whenever the record has never had one, so re-saving an untouched issuance was
 * enough to trip it.
 *
 * Dropping the key is also the right meaning for a `merge: true` write: absent
 * means *leave whatever is there alone*, which is what an untouched field
 * should do. Clearing a field is done with `''`, never with `undefined`, so
 * nothing here can silently swallow an intended erase.
 */
function writable<T extends object>(input: T): Partial<T> {
  return strip(input)
}

export async function createShareholder(input: ShareholderInput): Promise<string> {
  const ref = await addDoc(collection(db, SHAREHOLDERS), {
    ...writable(input),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateShareholder(id: string, patch: Partial<ShareholderInput>) {
  await setDoc(
    doc(db, SHAREHOLDERS, id),
    { ...writable(patch), updatedAt: serverTimestamp() },
    { merge: true },
  )
}

export async function deleteShareholder(id: string) {
  await deleteDoc(doc(db, SHAREHOLDERS, id))
}

/**
 * Replace the whole register with the rows from a CSV — decision 56.
 *
 * Not a merge, and the confirmation the user just passed said so. Every holder
 * not present in the file is deleted; every holder in it is written whole.
 *
 * ## One batch, so a half-replaced register cannot exist
 *
 * A loop of writes that failed in the middle would leave some holders replaced,
 * some deleted and some untouched, with no record of where it stopped — and the
 * file that could repair it is the one that just failed to apply. A batch is
 * atomic: it all lands or none of it does.
 *
 * Firestore caps a batch at 500 operations. The register is a dozen rows and a
 * company with 500 shareholders is not using a spreadsheet to manage them, so
 * the cap is a guard rather than a case to handle: over it, this refuses and
 * says so, which is far better than silently applying the first 500.
 */
export async function replaceRegister(
  rows: (ShareholderInput & { createdAt?: string })[],
  removeIds: string[],
) {
  const operations = rows.length + removeIds.length
  if (operations > 450) {
    throw new Error(
      `That file would take ${operations} writes, past what one atomic batch allows. ` +
        'Nothing was changed.',
    )
  }

  const batch = writeBatch(db)
  for (const id of removeIds) batch.delete(doc(db, SHAREHOLDERS, id))
  for (const row of rows) {
    const { id, createdAt, ...rest } = row as ShareholderInput & {
      id: string
      createdAt?: string
    }
    // `set` without merge, deliberately: the file is the register now, so a
    // field somebody cleared in Excel has to be cleared here rather than
    // surviving underneath because a merge left it alone.
    batch.set(doc(db, SHAREHOLDERS, id), {
      ...writable(rest),
      // Which is why this has to be written back rather than left out. The day
      // a holder came onto the register is a fact about them, not about this
      // write, and a `set` that omitted it would quietly re-date every existing
      // holder to the afternoon somebody fixed a typo in one row — decision 83.
      // `planImport` carries it through from the file or from the record it
      // matched by id; only a genuinely new row arrives without one.
      createdAt: createdAt ?? serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  }
  await batch.commit()
}

export async function createIssuance(input: IssuanceInput): Promise<string> {
  const ref = await addDoc(collection(db, ISSUANCES), {
    ...writable(input),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateIssuance(id: string, patch: Partial<IssuanceInput>) {
  await setDoc(
    doc(db, ISSUANCES, id),
    { ...writable(patch), updatedAt: serverTimestamp() },
    { merge: true },
  )
}

/**
 * An issuance with shares against it is not deletable from here.
 *
 * Deleting it would leave every purchase naming it pointing at nothing, and the
 * percentages beside them would keep counting shares from a round that no longer
 * exists. The screen refuses and says which holders are in the way.
 */
export async function deleteIssuance(id: string) {
  await deleteDoc(doc(db, ISSUANCES, id))
}

export async function createTransfer(input: TransferInput): Promise<string> {
  const ref = await addDoc(collection(db, TRANSFERS), {
    ...writable(input),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
  return ref.id
}

export async function updateTransfer(id: string, patch: Partial<TransferInput>) {
  await setDoc(
    doc(db, TRANSFERS, id),
    { ...writable(patch), updatedAt: serverTimestamp() },
    { merge: true },
  )
}

/**
 * A transfer can be deleted; a settled one being deleted moves the shares back.
 *
 * That is the intended behaviour and not an oversight: the ledger is the record,
 * so removing an entry from it un-does exactly what the entry did. A transfer
 * that happened and was later unwound is a *second* transfer the other way, and
 * the screen says so rather than offering a reverse button that looks like an
 * edit.
 */
export async function deleteTransfer(id: string) {
  await deleteDoc(doc(db, TRANSFERS, id))
}

export type { ShareholderInput, IssuanceInput, TransferInput }
